import { useCallback, useContext } from 'react';

import { User } from 'redux/schemas/models/my-account';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';

import { css } from '@emotion/react';
import { largeSpacing } from 'styles/global_defaults/scaffolding';
import { semiBoldFontWeight } from 'styles/global_defaults/fonts';

import { PILL_COLOR, PILL_MAX_WIDTH } from 'athena/components/mentoring-program/program-configuration/program-setup/alias-setting';

import Avatar from '../../../avatar';
import MentoringProgramContext from '../../../context';
import { gray2, gray6, success, white } from 'styles/global_defaults/colors';
import t from 'react-translate';
import { useMentorRequestContext } from '../mentor-request-modal/mentor-request-context';
import useMentorAvailability from 'athena/hooks/use-mentor-availability';

enum MENTOR_AVAILABILITY_STATUS {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
}

const MENTOR_AVAILABILITY_COLORS = {
  [MENTOR_AVAILABILITY_STATUS.AVAILABLE]: {
    background: success,
    text: white,
  },
  [MENTOR_AVAILABILITY_STATUS.UNAVAILABLE]: {
    background: gray6,
    text: gray2,
  },
}

const DEFAULT_PILL_COLOR = {
  background: success,
  text: white,
};

type AvatarPillType = {
  user: User;
  role: MentoringProgramRole;
  size?: number;
  showAvailability?: boolean;
};

const AvatarPill = ({
  user,
  role,
  size = 100,
  showAvailability = false,
}: AvatarPillType) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { programAlias } = mentoringProgram;
  const isMentor = role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR;
  const aliasRole = isMentor ? 'mentor' : 'mentee';
  const alias = programAlias?.[aliasRole]?.capitalizedSingularized;

  const { participant } = useMentorRequestContext();
  const {
    availability: participantAvailability,
    isAvailable: participantIsAvailable,
  } = useMentorAvailability(participant);
  const availability = participantAvailability || 0;
  const mentorAvailabilityStatus = participantIsAvailable
    ? MENTOR_AVAILABILITY_STATUS.AVAILABLE
    : MENTOR_AVAILABILITY_STATUS.UNAVAILABLE;

  const getAvailabilityText = useCallback(() => (
    t.MENTORING_PROGRAMS.AVATAR.MENTOR_AVAILABILITY[mentorAvailabilityStatus](
      mentorAvailabilityStatus === MENTOR_AVAILABILITY_STATUS.AVAILABLE ? availability.toString() : undefined,
    )
  ), [availability, mentorAvailabilityStatus]);

  const [text, colors] = showAvailability && isMentor
    ? [
      getAvailabilityText(),
      MENTOR_AVAILABILITY_COLORS[mentorAvailabilityStatus],
    ] : [
      alias,
      PILL_COLOR[role] || DEFAULT_PILL_COLOR,
    ];

  const {
    background: backgroundColor,
    text: textColor = '',
  } = colors;

  const styles = css`
    .avatar {
      height: ${size}px;
      width: ${size}px;
      border-radius: 50%;
    }
    .pill {
      height: ${largeSpacing}px;
      font-weight: ${semiBoldFontWeight};
      border-radius: ${largeSpacing / 2}px;
      color: ${textColor};
      background-color: ${backgroundColor};
      max-width: ${PILL_MAX_WIDTH}px;
      margin-top: -13px;
      div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  `;

  return (
    <div css={styles} className='d-flex align-items-center flex-column'>
      <Avatar
        showNameOnHover
        user={user}
        key={user.id}
        className='mr-1 avatar'
        role={role}
      />
      <div
        className='pill d-flex align-items-center pl-2 pr-2 text-small'
      >
        <div>{text}</div>
      </div>
    </div>
  );
};

export default AvatarPill;
